/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `useSessionUserRefresh` for the Caintra Connect frontend.
 * @module hooks/useSessionUserRefresh
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useEffect } from "react";
import {
  AUTH_SESSION_STATUS,
  useAuthSession,
} from "../context/AuthSessionContext";

/**
 * Keeps a local user state in sync with the shared auth session.
 * Visibility, focus, and route-change refresh are handled by AuthSessionProvider.
 *
 * @param {(user: object|null) => void} setUser - Local state setter for the current user
 * @param {Object} [options]
 * @param {boolean} [options.refreshOnMount=false] - Background refresh once when session is ready
 * @returns {() => Promise<object|null>} Manual refresh function
 */
const useSessionUserRefresh = (setUser, { refreshOnMount = false } = {}) => {
  const { user, refreshSession, status } = useAuthSession();

  useEffect(() => {
    if (setUser) {
      setUser(user);
    }
  }, [setUser, user]);

  useEffect(() => {
    if (refreshOnMount && status === AUTH_SESSION_STATUS.READY) {
      refreshSession({ background: true });
    }
  }, [refreshOnMount, refreshSession, status]);

  return refreshSession;
};

export default useSessionUserRefresh;
